/**
 * F38 (Wave 2 Track D) — admin per-venue subscription detail page.
 *
 * Route: `/venue-subscriptions/:venueId`
 * Permission: `payments:read` (view) + `venue_subscription:override` (actions).
 *
 * Shows the venue's current plan (tier / cadence / status / renews-on Stats +
 * queued-switch + trial + past-due banners), the plan-history AuditLogTimeline,
 * and the venue's subscription invoices inline (linking each row to the F23
 * /commission-statements detail). Override / Grant trial / Manual invoice CTAs
 * open their dialogs; hidden for actors without `venue_subscription:override`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  type AuditEvent,
  AuditLogTimeline,
  Badge,
  Banner,
  Button,
  Chip,
  CurrencyDisplay,
  EmptyState,
  InvoiceStatusBadge,
  type InvoiceSource,
  type InvoiceStatus,
  PlanTierBadge,
  Skeleton,
  SourceTag,
  Stat,
  StatusChip,
  SubStateBadge,
  type SubState,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import type {
  AdminOverrideSubscriptionInput,
  GrantTrialInput,
  ManualSubscriptionInvoiceInput,
  VenueSubscriptionStatus,
} from '@bravemove/types';
import {
  adminSubscriptionApi,
  type AdminVenueSubscriptionDetail,
  type AdminSubscriptionInvoice,
} from '../../api/subscription';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { OverrideSubscriptionDialog } from '../../components/subscription/OverrideSubscriptionDialog';
import { GrantTrialDialog } from '../../components/subscription/GrantTrialDialog';
import { ManualInvoiceDialog } from '../../components/subscription/ManualInvoiceDialog';

/**
 * The F38 `SubStateBadge` primitive covers the four lifecycle states; `suspended`
 * is an admin-only terminal state with no primitive variant, so it keeps the
 * `StatusChip` (rejected tone).
 */
function isSubStateBadgeState(status: VenueSubscriptionStatus): status is SubState {
  return status !== 'suspended';
}

/**
 * Known invoice (commission-statement) billing sources rendered with `SourceTag`.
 * The invoice `source` is typed `string` on the API client; unknown values fall
 * back to a plain label.
 */
const KNOWN_INVOICE_SOURCES: readonly InvoiceSource[] = [
  'venue_subscription',
  'venue_hybrid',
  'venue_commission',
  'venue_placement',
  'tournament_listing',
  'tournament_ticketing_commission',
];

function asInvoiceSource(source: string): InvoiceSource | null {
  return KNOWN_INVOICE_SOURCES.includes(source as InvoiceSource) ? (source as InvoiceSource) : null;
}

/**
 * Map the F23 statement status (`draft | issued | settled | void`) onto the
 * `InvoiceStatusBadge` variants. `settled` reads as `paid`; `void` has no badge
 * variant and falls back to a plain label.
 */
function asInvoiceStatus(status: string): InvoiceStatus | null {
  switch (status) {
    case 'draft':
      return 'draft';
    case 'issued':
      return 'issued';
    case 'settled':
      return 'paid';
    default:
      return null;
  }
}

const AUDIT_KIND_MAP: Record<string, AuditEvent['kind']> = {
  created: 'create',
  trial_started: 'create',
  admin_trial_grant: 'create',
  trial_ended_downgraded: 'update',
  switch_queued: 'update',
  switch_cancelled: 'update',
  switch_effective: 'update',
  reinstated: 'update',
  reactivated: 'update',
  past_due: 'alert',
  cancelled: 'delete',
  suspended: 'alert',
  admin_override: 'permission',
};

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatPeriod(periodStart: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      timeZone: 'Asia/Beirut',
    }).format(new Date(periodStart));
  } catch {
    return periodStart;
  }
}

export function VenueSubscriptionDetailPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const params = useParams<{ venueId: string }>();
  const venueId = params.venueId ?? '';

  const canOverride = auth.adminUser?.permissions?.includes('venue_subscription:override') ?? false;

  const [detail, setDetail] = useState<AdminVenueSubscriptionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [grantTrialOpen, setGrantTrialOpen] = useState(false);
  const [manualInvoiceOpen, setManualInvoiceOpen] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await adminSubscriptionApi.getVenueSubscription(venueId));
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [venueId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  const auditEvents = useMemo<readonly AuditEvent[]>(() => {
    if (!detail) return [];
    return detail.history.map((h) => ({
      id: h.id,
      at: h.createdAt,
      kind: AUDIT_KIND_MAP[h.eventKind] ?? 'info',
      summary: t(`admin:subscription.history.${h.eventKind}`, { defaultValue: h.eventKind }),
      actor: {
        name:
          h.actorLabel ?? t('admin:subscription.history.actor_system', { defaultValue: 'System' }),
      },
      ...(h.reason ? { details: h.reason } : {}),
    }));
  }, [detail, t]);

  const invoiceColumns = useMemo<readonly TableColumn<AdminSubscriptionInvoice>[]>(
    () => [
      {
        key: 'period',
        header: t('admin:subscription.invoices.col_period', { defaultValue: 'Period' }),
        cell: (row) => (
          <span className="text-[13px] text-ink">{formatPeriod(row.periodStart, locale)}</span>
        ),
      },
      {
        key: 'source',
        header: t('admin:subscription.invoices.col_source', { defaultValue: 'Type' }),
        cell: (row) => {
          const src = asInvoiceSource(row.source);
          return src ? (
            <SourceTag
              source={src}
              label={t(`commission:source.${row.source}`, { defaultValue: row.source })}
            />
          ) : (
            <span className="text-[12.5px] text-ink-2">
              {t(`commission:source.${row.source}`, { defaultValue: row.source })}
            </span>
          );
        },
      },
      {
        key: 'cadence',
        header: t('admin:subscription.invoices.col_cadence', { defaultValue: 'Cadence' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">
            {row.billingCadence
              ? t(`admin:subscription.cadence.${row.billingCadence}`, {
                  defaultValue: row.billingCadence,
                })
              : '—'}
          </span>
        ),
      },
      {
        key: 'amount',
        header: t('admin:subscription.invoices.col_amount', { defaultValue: 'Amount' }),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay amount={row.subscriptionFeeAmount} currency="USD" locale={locale} />
        ),
      },
      {
        key: 'status',
        header: t('admin:subscription.invoices.col_status', { defaultValue: 'Status' }),
        cell: (row) => {
          const st = asInvoiceStatus(row.status);
          return st ? (
            <InvoiceStatusBadge
              size="sm"
              status={st}
              label={t(`commission:status.${row.status}`, { defaultValue: row.status })}
            />
          ) : (
            <span className="text-[12px] text-ink-2">
              {t(`commission:status.${row.status}`, { defaultValue: row.status })}
            </span>
          );
        },
      },
    ],
    [t, locale],
  );

  const onConfirmOverride = async (input: AdminOverrideSubscriptionInput) => {
    await adminSubscriptionApi.overrideSubscription(venueId, input);
    toast.push({
      tone: 'success',
      title: t('admin:subscription.toast.override_applied', { defaultValue: 'Override applied.' }),
    });
    void fetchDetail();
  };

  const onConfirmGrantTrial = async (input: GrantTrialInput) => {
    await adminSubscriptionApi.grantTrial(venueId, input);
    toast.push({
      tone: 'success',
      title: t('admin:subscription.toast.trial_granted', { defaultValue: 'Trial granted.' }),
    });
    void fetchDetail();
  };

  const onConfirmManualInvoice = async (input: ManualSubscriptionInvoiceInput) => {
    await adminSubscriptionApi.createManualInvoice(venueId, input);
    toast.push({
      tone: 'success',
      title: t('admin:subscription.toast.invoice_created', { defaultValue: 'Invoice created.' }),
    });
    void fetchDetail();
  };

  const venueLabel = detail?.venueName ?? venueId;

  return (
    <div className="space-y-6" data-testid="venue-subscription-detail-page">
      <PageHeader
        title={venueLabel}
        eyebrow={t('admin:subscription.detail.eyebrow', { defaultValue: 'Subscription' })}
        actions={
          canOverride && detail ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setGrantTrialOpen(true)}
                data-testid="venue-subscription-grant-trial-cta"
              >
                {t('admin:subscription.detail.grant_trial', { defaultValue: 'Grant trial' })}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setManualInvoiceOpen(true)}
                data-testid="venue-subscription-manual-invoice-cta"
              >
                {t('admin:subscription.detail.manual_invoice', { defaultValue: 'Manual invoice' })}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setOverrideOpen(true)}
                data-testid="venue-subscription-override-cta"
              >
                {t('admin:subscription.detail.override', { defaultValue: 'Override' })}
              </Button>
            </>
          ) : !canOverride ? (
            <Chip data-testid="venue-subscription-readonly">
              {t('admin:subscription.detail.readonly', { defaultValue: 'Read-only' })}
            </Chip>
          ) : null
        }
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="venue-subscription-detail-loading">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : !detail ? (
        <EmptyState
          title={t('admin:subscription.detail.not_found', {
            defaultValue: 'No subscription found for this venue.',
          })}
        />
      ) : (
        <>
          {detail.status === 'past_due' ? (
            <div data-testid="venue-subscription-past-due-banner">
              <Banner tone="warning">
                {t('admin:subscription.detail.past_due_banner', {
                  date: formatDate(detail.pastDueSince, locale),
                  defaultValue:
                    'Past due since {date} — tier features lock after the grace window.',
                })}
              </Banner>
            </div>
          ) : null}

          {detail.queuedSwitchTo ? (
            <div data-testid="venue-subscription-queued-banner">
              <Banner tone="info">
                {t('admin:subscription.detail.queued_banner', {
                  tier: t(`admin:subscription.tier.${detail.queuedSwitchTo}`, {
                    defaultValue: detail.queuedSwitchTo,
                  }),
                  date: formatDate(detail.queuedEffectiveAt, locale),
                  defaultValue: 'Switch to {tier} queued for {date}.',
                })}
              </Banner>
            </div>
          ) : null}

          {detail.cancelAtPeriodEnd ? (
            <div data-testid="venue-subscription-cancel-banner">
              <Banner tone="info">
                {t('admin:subscription.detail.cancel_banner', {
                  date: formatDate(detail.currentPeriodEnd, locale),
                  defaultValue: 'Cancellation scheduled — reverts to Free on {date}.',
                })}
              </Banner>
            </div>
          ) : null}

          {/* Current plan summary */}
          <section
            className="grid grid-cols-2 gap-3 md:grid-cols-4"
            data-testid="venue-subscription-current"
          >
            <Stat
              label={t('admin:subscription.detail.tier', { defaultValue: 'Tier' })}
              value={
                <PlanTierBadge
                  tier={detail.tierCode}
                  cadence={detail.billingCadence}
                  label={t(`admin:subscription.tier.${detail.tierCode}`, {
                    defaultValue: detail.tierCode,
                  })}
                />
              }
            />
            <Stat
              label={t('admin:subscription.detail.status', { defaultValue: 'Status' })}
              value={
                isSubStateBadgeState(detail.status) ? (
                  <SubStateBadge
                    size="sm"
                    state={detail.status}
                    label={t(`admin:subscription.status.${detail.status}`, {
                      defaultValue: detail.status,
                    })}
                  />
                ) : (
                  <StatusChip
                    size="sm"
                    status="rejected"
                    label={t(`admin:subscription.status.${detail.status}`, {
                      defaultValue: detail.status,
                    })}
                  />
                )
              }
            />
            <Stat
              label={t('admin:subscription.detail.cadence', { defaultValue: 'Cadence' })}
              value={t(`admin:subscription.cadence.${detail.billingCadence}`, {
                defaultValue: detail.billingCadence,
              })}
            />
            <Stat
              label={t('admin:subscription.detail.renews', { defaultValue: 'Renews / ends' })}
              value={formatDate(detail.currentPeriodEnd, locale)}
            />
          </section>

          {detail.status === 'trialing' && detail.trialEndsAt ? (
            <div data-testid="venue-subscription-trial-banner">
              <Banner tone="info">
                {t('admin:subscription.detail.trial_banner', {
                  date: formatDate(detail.trialEndsAt, locale),
                  defaultValue: 'Trial ends {date}.',
                })}
              </Banner>
            </div>
          ) : null}

          {/* Plan history */}
          <section className="space-y-3" data-testid="venue-subscription-history">
            <h2 className="text-[14px] font-semibold text-ink">
              {t('admin:subscription.detail.history_heading', { defaultValue: 'Plan history' })}
            </h2>
            {auditEvents.length === 0 ? (
              <EmptyState
                title={t('admin:subscription.detail.history_empty', {
                  defaultValue: 'No plan history yet.',
                })}
              />
            ) : (
              <AuditLogTimeline events={auditEvents} />
            )}
          </section>

          {/* Inline invoices */}
          <section className="space-y-3" data-testid="venue-subscription-invoices">
            <h2 className="text-[14px] font-semibold text-ink">
              {t('admin:subscription.detail.invoices_heading', { defaultValue: 'Invoices' })}
            </h2>
            {detail.invoices.length === 0 ? (
              <EmptyState
                title={t('admin:subscription.detail.invoices_empty', {
                  defaultValue: 'No subscription invoices yet.',
                })}
              />
            ) : (
              <Table
                data={detail.invoices as AdminSubscriptionInvoice[]}
                columns={invoiceColumns as TableColumn<AdminSubscriptionInvoice>[]}
                rowKey={(row) => row.id}
                onRowPress={(row) => navigate(`/commission-statements/${row.id}`)}
              />
            )}
          </section>

          <p className="text-[11px] text-ink-3">
            <Badge tone="neutral" size="sm">
              {t('admin:subscription.detail.off_platform_note', {
                defaultValue:
                  'Off-platform billing — settle invoices in the commission statements queue.',
              })}
            </Badge>
          </p>
        </>
      )}

      {detail && overrideOpen ? (
        <OverrideSubscriptionDialog
          open={overrideOpen}
          venueLabel={venueLabel}
          currentTierCode={detail.tierCode}
          currentCadence={detail.billingCadence}
          currentStatus={detail.status}
          onConfirm={onConfirmOverride}
          onClose={() => setOverrideOpen(false)}
        />
      ) : null}

      {detail && grantTrialOpen ? (
        <GrantTrialDialog
          open={grantTrialOpen}
          venueLabel={venueLabel}
          onConfirm={onConfirmGrantTrial}
          onClose={() => setGrantTrialOpen(false)}
        />
      ) : null}

      {detail && manualInvoiceOpen ? (
        <ManualInvoiceDialog
          open={manualInvoiceOpen}
          venueLabel={venueLabel}
          onConfirm={onConfirmManualInvoice}
          onClose={() => setManualInvoiceOpen(false)}
        />
      ) : null}
    </div>
  );
}

export default VenueSubscriptionDetailPage;
